import { useMemo } from 'react';
import { OnboardingProgressV1 } from '@frontend/api-onboarding-progress';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { OnboardingModuleTasksConfig } from '../types';
import { getModuleTaskStats } from '../utils';
import { useHasOnboardingV2FeatureFlag } from './use-has-onboarding-v2-feature-flag';
import { useOnboardingTasksSkip } from './use-onboarding-tasks-skip';

const CACHE_TIME = 1000 * 60 * 5; // 5 minutes

export const useOnboardingDashboardConfig = ({
  locationId,
}: { locationId?: string } = {}): OnboardingModuleTasksConfig => {
  const { isSingleTypeScope, selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = locationId || selectedLocationIds?.[0];

  const { isEnabled: isFFEnabled, isLoading: isFFLoading } = useHasOnboardingV2FeatureFlag({
    locationIds: [selectedLocationId],
  });

  const isLocationEligibleForV2Onboarding = isFFEnabled && isSingleTypeScope;

  /* Skipped Tasks check */
  const { isLoading: isTasksSkippedLoading, isOnboardingTasksSkipped } = useOnboardingTasksSkip({
    locationId: selectedLocationId,
  });

  // For fast pass locations onboarding progress and its status can be used to determine fast pass eligibility
  const { data: isFastPassLocation, isLoading: isLoadingOnboardingProgress } =
    OnboardingProgressV1.Queries.useGetLatestOnboardingProgressByLocationIDQuery({
      request: { locationId: selectedLocationId },
      options: {
        retry: false,
        enabled: isLocationEligibleForV2Onboarding,
        cacheTime: CACHE_TIME,
        staleTime: CACHE_TIME,
        select: (data) =>
          data?.onboardingProgressStatus &&
          data.onboardingProgressStatus !==
            OnboardingProgressV1.Types.OnboardingProgressStatus.ONBOARDING_PROGRESS_STATUS_UNSPECIFIED,
      },
      httpOptions: {
        onFetchError: (error) => {
          if (error.status && error.status !== 401) {
            sentry.warn({
              error,
              team: 'fe-activation',
              topic: 'onboarding',
              addContext: {
                name: 'details',
                context: {
                  locationId: selectedLocationId,
                  message: 'Failed to fetch onboarding progress from use-onboarding-dashboard-config',
                },
              },
            });
          }
        },
      },
    });

  /* Module Tasks Data fetch */
  const shouldFetchModuleTasks: boolean =
    isLocationEligibleForV2Onboarding && !!isFastPassLocation && !isTasksSkippedLoading && !isOnboardingTasksSkipped;
  const { data: onboardingModuleTasksResponse, isLoading: isOnboardingModuleTasksLoading } =
    OnboardingTasksV2.Queries.useGetModulesAndTasksByLocationIdQuery({
      request: { locationId: selectedLocationId },
      options: {
        retry: false,
        enabled: shouldFetchModuleTasks,
        cacheTime: CACHE_TIME,
        staleTime: CACHE_TIME,
      },
    });

  const isLoading =
    isOnboardingModuleTasksLoading || isFFLoading || isLoadingOnboardingProgress || isTasksSkippedLoading;

  const enableSkipTasks = !!onboardingModuleTasksResponse?.enableSkipTasks;
  const tasksDurationExpired = !!onboardingModuleTasksResponse?.tasksDurationExpired;
  const isReady = !isLoading && (!shouldFetchModuleTasks || !!onboardingModuleTasksResponse);

  return useMemo<OnboardingModuleTasksConfig>(() => {
    const onboardingModuleTasks = onboardingModuleTasksResponse?.modules ?? [];

    const { pending: pendingTasks } = getModuleTaskStats(onboardingModuleTasks);
    const showOnboardingDashboard = shouldFetchModuleTasks && isReady && !tasksDurationExpired && pendingTasks > 0;

    // Enable skip tasks is also an indicator that handoff is completed or 30 days have passed since the onboarding started
    // If skip tasks is enabled, default redirection to onboarding dashboard is not required
    const shouldRedirectToOnboarding = showOnboardingDashboard && !enableSkipTasks;

    return {
      showOnboardingDashboard,
      isLoading,
      onboardingModuleTasks,
      isReady,
      selectedLocationId,
      enableSkipTasks,
      shouldRedirectToOnboarding,
      isLocationEligibleForV2Onboarding,
    };
  }, [
    shouldFetchModuleTasks,
    onboardingModuleTasksResponse?.modules,
    tasksDurationExpired,
    enableSkipTasks,
    isLoading,
    isReady,
    selectedLocationId,
    isLocationEligibleForV2Onboarding,
  ]);
};
